import { defineComponent as _defineComponent } from 'vue'
import { mergeProps as _mergeProps, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import videojs from "video.js";
import { computed, ref, watch } from "vue";
import OaiVideoPlayer from "shared/components/camera/OaiVideoPlayer.vue";
import {
  CustomPlayer,
  Milestone,
  useDailyTimelapsePlayerMilestones,
} from "shared/composables/videoPlayer/milestones";
import {
  ProcessBox,
  useDailyTimelapsePlayerProcessBoxes,
} from "shared/composables/videoPlayer/processBoxes";
import { DailyTimelapseRange } from "shared/types/Camera";
import { OaiVideoPlayerProps } from "shared/types/VideoPlayer";

const TIMELAPSE_FRAME_INTERVAL = 5 * 1000;


export default /*#__PURE__*/_defineComponent({
  __name: 'TimelapseVideoPlayer',
  props: {
    keepControls: { type: Boolean },
    timestamps: { default: () => [] },
    milestones: { default: () => [] },
    processBoxes: { default: () => [] },
    highlightedBoxIndex: {},
    showBoxes: { type: Boolean, default: true },
    formatTime: {},
    formatMilestone: {},
    src: {},
    loading: { type: Boolean },
    noSrcMessage: {},
    hls: { type: Boolean },
    autoPlay: { type: Boolean },
    addRightSpaceToControlBar: { type: Boolean }
  },
  emits: ["play"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const playerProps = computed(() => {
  const {
    milestones,
    timestamps,
    keepControls,
    processBoxes,
    showBoxes,
    highlightedBoxIndex,
    formatTime,
    formatMilestone,
    ...rest
  } = props;

  return rest;
});

const timestamps = computed(() => parseTimelapseRanges(props.timestamps));
const milestones = computed(() => props.milestones);
const processBoxes = computed(() => props.processBoxes);
const highlightedBoxIndex = computed(() => props.highlightedBoxIndex);

const videojsPlayer = ref<CustomPlayer>();

const { setKeepControls, clearMilestonesAddons, setMilestonesAddons, jumpToMilestone } =
  useDailyTimelapsePlayerMilestones(
    videojsPlayer,
    timestamps,
    milestones,
    props.formatTime,
    props.formatMilestone,
  );
const { setProcessBoxesAddons, clearProcessBoxesAddons } = useDailyTimelapsePlayerProcessBoxes(
  videojsPlayer,
  processBoxes,
  highlightedBoxIndex,
  props.showBoxes,
);

const initMilestonesAddons = () => {
  clearMilestonesAddons();
  if (!videojsPlayer.value || videojsPlayer.value.isDisposed()) {
    return;
  }

  setMilestonesAddons();
};

const initProcessBoxesAddons = () => {
  clearProcessBoxesAddons();

  if (!videojsPlayer.value || videojsPlayer.value.isDisposed()) {
    return;
  }

  setProcessBoxesAddons();
};

const setNewAddons = () => {
  if (!videojsPlayer.value || videojsPlayer.value.isDisposed()) {
    return;
  }

  initMilestonesAddons();
  initProcessBoxesAddons();
};

const handleMounted = (player: videojs.Player) => {
  videojsPlayer.value = player as CustomPlayer;

  player.one("durationchange", setNewAddons);
  if (props.keepControls) {
    setKeepControls();
  }
};

watch(
  () => [props.timestamps.length, props.milestones, props.processBoxes],
  () => {
    initMilestonesAddons();
    initProcessBoxesAddons();
  },
);

__expose({
  jumpToMilestone,
});

const parseTimelapseRanges = (compressedTimestamps: DailyTimelapseRange[] | Date[]): Date[] => {
  if (compressedTimestamps[0] instanceof Date) {
    return compressedTimestamps as Date[];
  }

  const fullTimestamps: Date[] = [];
  const ranges = compressedTimestamps as DailyTimelapseRange[];

  ranges.forEach(([start, end]) => {
    const frameTime = new Date(start.getTime() - TIMELAPSE_FRAME_INTERVAL);
    for (let i = 0; frameTime.getTime() < end.getTime(); i++) {
      frameTime.setTime(frameTime.getTime() + TIMELAPSE_FRAME_INTERVAL);
      if (frameTime.getTime() <= end.getTime()) {
        fullTimestamps.push(new Date(frameTime.getTime()));
      }
    }
  });

  return fullTimestamps;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(OaiVideoPlayer, _mergeProps(playerProps.value, {
    onMounted: handleMounted,
    onPlay: _cache[0] || (_cache[0] = ($event: any) => (emit('play')))
  }), null, 16))
}
}

})